import React from 'react'
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Stack from '@mui/material/Stack';
import { SxProps, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { icons } from '../../assets/icons/icons';
import { colors } from '../../constants/colors';

type menuProps = {
    id: number;
    name: string;
    onClick?: () => void;
}
type Props = {
    menuItems: menuProps[];
    onClick?: () => void;
    buttonName: string;
    heading?: string;
    headingNormal?: boolean;
    headingAlign?: React.CSSProperties['textAlign'];
    sx?: SxProps;
    fullWidth?: boolean;
}

const DropdownButton: React.FC<Props> = (props: Props) => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const [menuWidth, setMenuWidth] = React.useState<number | undefined>(undefined);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpen(false);
        } else if (event.key === 'Escape') {
            setOpen(false);
        }
    }
    const prevOpen = React.useRef(open);
    React.useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
        }

        prevOpen.current = open;
    }, [open]);
    React.useEffect(() => {
        if (anchorRef.current) {
            setMenuWidth(anchorRef.current.offsetWidth); // Set the width of the menu to match the button
        }
    }, [anchorRef.current]);

    return (
        <div className='w-full'>
            {props.heading && (
                <div
                    className="mt-1"
                    style={{ textAlign: props.headingAlign || "left" }}
                >
                    <Typography variant={props.headingNormal ? 'subtitle1' : 'body1'}>
                        {props.heading}
                    </Typography>
                </div>
            )}
            <Stack direction="row" spacing={2}>
                <div className='w-full'>
                    <Button
                        sx={{
                            ...props.sx,
                            textTransform: "none",
                            width: "100%",
                            maxWidth: props.fullWidth ? '100%' : "160px",
                            height: "43.5px",
                            borderRadius: "8px",
                            backgroundColor: colors.dropdown_button,
                            color: colors.white,
                            border: `1px solid #E5E5E7`,
                            '&:hover': {
                                backgroundColor: colors.dropdown_button
                            }
                        }}
                        startIcon={<img src={icons.download} alt='start-icon' />}
                        endIcon={<KeyboardArrowDownIcon />}
                        ref={anchorRef}
                        id="composition-button"
                        aria-controls={open ? 'composition-menu' : undefined}
                        aria-expanded={open ? 'true' : undefined}
                        aria-haspopup="true"
                        onClick={handleToggle}
                    >
                        {props.buttonName}
                    </Button>
                    <Popper
                        sx={{ zIndex: 9999 }}
                        open={open}
                        anchorEl={anchorRef.current}
                        role={undefined}
                        placement="bottom-start"
                        transition
                        disablePortal
                    >
                        {({ TransitionProps, placement }) => (
                            <Grow
                                {...TransitionProps}
                                style={{
                                    transformOrigin:
                                        placement === 'bottom-start' ? 'left top' : 'left bottom',
                                }}
                            >
                                <Paper sx={{ width: menuWidth, textAlign: 'center' }}>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuList
                                            autoFocusItem={open}
                                            id="composition-menu"
                                            aria-labelledby="composition-button"
                                            onKeyDown={handleListKeyDown}
                                        >
                                            {props.menuItems?.map((list) => (
                                                <MenuItem onClick={() => {
                                                    list.onClick && list.onClick();
                                                    setOpen(false);
                                                }} key={list.id}>
                                                    {list.name}
                                                </MenuItem>
                                            ))}

                                        </MenuList>
                                    </ClickAwayListener>
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </div>
            </Stack>
        </div>
    )
}

export default DropdownButton